/* eslint-disable */
import { stringify } from 'query-string';
import { fetchUtils } from 'react-admin';

// Define constants as strings for react-admin 3.x compatibility
const GET_LIST = 'GET_LIST';
const GET_ONE = 'GET_ONE';
const GET_MANY = 'GET_MANY';
const GET_MANY_REFERENCE = 'GET_MANY_REFERENCE';
const CREATE = 'CREATE';
const UPDATE = 'UPDATE';
const UPDATE_MANY = 'UPDATE_MANY';
const DELETE = 'DELETE';
const DELETE_MANY = 'DELETE_MANY';

/**
 * Maps react-admin queries to a simple REST API
 *
 * The REST dialect is similar to the one of FakeRest
 * @see https://github.com/marmelab/FakeRest
 * @example
 * GET_LIST     => GET http://my.api.url/posts?sort=['title','ASC']&range=[0, 24]
 * GET_ONE      => GET http://my.api.url/posts/123
 * GET_MANY     => GET http://my.api.url/posts?filter={ids:[123,456,789]}
 * UPDATE       => PUT http://my.api.url/posts/123
 * CREATE       => POST http://my.api.url/posts
 * DELETE       => DELETE http://my.api.url/posts/123
 */
export default (apiUrl, httpClient = fetchUtils.fetchJson) => {
  /**
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The data request params, depending on the type
   * @returns {Object} { url, options } The HTTP request parameters
   */
  const convertDataRequestToHTTP = (type, resource, params) => {
    // @fix for admin resource
    // react admin doesn't see the same resources and we should proxy admins to users
    if (resource === 'admins') {
      resource = 'users';
    }
    let url = '';
    const options = {};
    switch (type) {
      case GET_LIST: {
        const { page, perPage } = params.pagination;
        const { ...additionalFields } = params.additionalFields || {};
        let queryString = '';
        if (resource !== 'admin/advertisement') {
          const query = {
            limit: perPage,
            offset: (page - 1) * perPage,
            ...additionalFields,
            ...params.filter,
            sort: params.sort.field,
            order: params.sort.order,
          };
          queryString = `?${stringify(query)}`
        }

        options.method = 'GET';
        url = `${apiUrl}/${resource}${queryString}`;
        break;
      }
      case GET_ONE:
        options.method = 'GET';
        url = `${apiUrl}/${resource}`;
        if (params && params.id) {
          url = `${url}/${params.id}`;
        }
        if (params && params.query) {
          url = `${url}?${stringify(params.query)}`;
        }
        break;
      case GET_MANY: {
        const query = {};
        options.method = 'GET';
        url = `${apiUrl}/${resource}?${stringify(query)}`;
        break;
      }
      case GET_MANY_REFERENCE: {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = {
          sort: JSON.stringify([field, order]),
          range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
          filter: JSON.stringify({
            // ...params.filter,
            [params.target]: params.id,
          }),
        };
        options.method = 'GET';
        url = `${apiUrl}/${resource}`;
        if (params.target) {
          url = `${url}/${params.id}/${params.target}`;
        }
        break;
      }
      case 'GET_ALL':
        options.method = 'GET';
        url = `${apiUrl}/${resource}`;
        if (params?.urlParams) {
          options.urlParams = params.urlParams;
        }
        break;
      case UPDATE:
        url = `${apiUrl}/${resource}/${params.id}`;
        options.method = 'PUT';
        // options.body = JSON.stringify(params.data);
        options.body = params.data;
        break;
      case CREATE:
        url = `${apiUrl}/${resource}`;
        options.method = 'POST';
        // options.body = JSON.stringify(params.data);
        options.body = params.data;
        break;
      case DELETE:
        url = `${apiUrl}/${resource}/${params.id}`;
        options.method = 'DELETE';
        break;
      /// ///////////////
      // custom actions
      /// //////////////
      case 'CREATE_MUX_UPLOAD':
        url = `${apiUrl}/${resource}`;
        options.method = 'POST';
        break;
      case 'UPLOAD_FILE':
        url = `${apiUrl}/${resource}`;
        options.method = 'POST';
        options.body = params.data;
        options.onUploadProgress = params.onUploadProgress || null;
        break;
      case 'CREATE_ANNOUNCEMENT':
        url = `${apiUrl}/${resource}/${params.id}`;
        options.method = 'POST';
        options.body = params.data;
        break;
      default:
        throw new Error(`Unsupported fetch action type ${type}`);
    }
    return { url, options };
  };

  /**
   * @param {Object} response HTTP response from fetch()
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The data request params, depending on the type
   * @returns {Object} Data response
   */
  const convertHTTPResponse = (response, type, resource, params) => {
    const { data } = response;
    // @fix for admin resource
    // react admin doesn't see the same resources and we should proxy admins to users
    if (resource === 'admins') {
      resource = 'users';
    }
    switch (type) {
      case GET_LIST:
        if (resource === 'achievements') {
          return {
            data: data.achievements,
            total: data.total,
          };
        }
        if (resource === 'sessions') {
          return {
            data: data.sessions,
            total: data.total,
          };
        }
        if (resource === 'file') {
          return {
            data: data.items,
            total: data.total,
          };
        }
        if (resource === 'file/video') {
          const videosWithExercises = data.videos.map((video) => {
            const videoExercise = data.videoExercises.find((ve) => ve.videoId === video.id);
            return {
              ...video,
              exercises: videoExercise.exercises,
            };
          });

          return {
            data: videosWithExercises,
            total: data.total,
          };
        }
      case GET_MANY_REFERENCE:
        // if we are looking for nested resources
        if (params.target && params.target === 'sessions') {
          return {
            data: data.sessions,
            total: data.total,
          };
        }
        if (params.target && params.target === 'stories') {
          return {
            data: data.items,
            total: data.total,
          };
        }
        if (resource === 'users') {
          return {
            data: data.users,
            total: data.total,
          };
        }
        if (resource === 'exercise') {
          return {
            data: data.items,
            total: data.total,
          };
        }
        if (resource === 'programs') {
          const newList = data.programs.map((item) => {
            const { introScreens, ...rest } = item;
            return rest;
          });
          return {
            data: newList, // data.programs,
            total: data.total,
          };
        }
        if (resource === 'admin/products') {
          return {
            data: data.products,
            total: data.total,
          };
        }
        if (resource === 'admin/advertisement') {
          return {
            data: data.products,
            total: data.total,
          };
        }
        if (resource === 'admin/questions') {
          return {
            data: data.questions,
            total: data.total,
          };
        }
        if (resource.search('stories') !== -1) {
          return {
            data: data.items,
            total: data.total,
          };
        }
        // default return
        return { data };
      case GET_MANY:
        if (resource === 'admin/products/programs') {
          return {
            data: data.data,
            total: data.total,
          };
        }
        if (resource === 'sessions' && Array.isArray(data.sessions)) {
          return {
            data: data.sessions,
            total: data.total,
          };
        }
        if (Array.isArray(data?.data)) {
          return {
            data: data.data,
            total: data.total,
          };
        }
        if (Array.isArray(data?.items)) {
          return {
            data: data.items,
            total: data.total,
          };
        }
        if (Array.isArray(data)) {
          return { data };
        }
        if (resource.search('stories')) {
          return {
            data: data.items,
            total: data.total,
          };
        }
        return { data };
      case CREATE:
        // Special case for bulk-grant programs
        if (resource === 'programs/bulk-grant') {
          return { data: data.results };
        }
        return { data: { ...params.data, id: data.id } };
      case DELETE:
        return { data: { ...params.data, id: params.id } };
      default:
        return { data };
    }
  };

  /**
   * @param {string} type Request type, e.g GET_LIST
   * @param {string} resource Resource name, e.g. "posts"
   * @param {Object} payload Request parameters. Depends on the request type
   * @returns {Promise} the Promise for a data response
   */
  return (type, resource, params) => {
    // React-Admin 2.x issues a GET_MANY when ReferenceInput needs to hydrate the
    // currently selected value. The /file/video collection endpoint only supports
    // pagination/search, so we fan out into individual GET calls to make sure
    // React-Admin receives an array containing the requested records.
    if (type === GET_MANY && resource === 'file/video') {
      if (!params.ids || params.ids.length === 0) {
        return Promise.resolve({ data: [] });
      }

      // Try batch endpoint first
      const filter = JSON.stringify({ ids: params.ids });
      const batchUrl = `${apiUrl}/${resource}?filter=${filter}`;
      return httpClient(batchUrl, { method: 'GET' })
        .then((response) => {
          // If batch endpoint returns data, use it
          if (Array.isArray(response.data)) {
            return { data: response.data };
          }
          // If not, fallback to individual requests
          throw new Error('Batch endpoint did not return array');
        })
        .catch(() => {
          // Fallback: fan out into individual GET calls
          const requests = params.ids.map((id) =>
            httpClient(`${apiUrl}/${resource}/${id}`, {
              method: 'GET',
            })
              .then((response) => response.data)
              .catch((error) => {
                if (error?.response?.status === 404) {
                  return null;
                }
                throw error;
              }),
          );
          return Promise.all(requests).then((records) => ({
            data: records.filter((record) => record !== null && typeof record !== 'undefined'),
          }));
        });
    }

    // simple-rest doesn't handle filters on UPDATE route, so we fallback to calling UPDATE n times instead
    if (type === UPDATE_MANY) {
      return Promise.all(
        params.ids.map((id) =>
          httpClient(`${apiUrl}/${resource}/${id}`, {
            method: 'PUT',
            body: JSON.stringify(params.data),
          }),
        ),
      ).then((responses) => ({
        data: responses.map((response) => response.json),
      }));
    }
    // simple-rest doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
    if (type === DELETE_MANY) {
      return Promise.all(
        params.ids.map((id) =>
          httpClient(`${apiUrl}/${resource}/${id}`, {
            method: 'DELETE',
          }),
        ),
      ).then((responses) => ({
        data: responses.map((response) => response.json),
      }));
    }
    const { url, options } = convertDataRequestToHTTP(type, resource, params);
    return httpClient(url, options).then((response) => {
      return convertHTTPResponse(response, type, resource, params);
    });
  };
};
